import get from "lodash/get";
import React, {Component} from "react";
import compose from "lodash/flowRight";
import {graphql} from "react-apollo";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {VisuallyHidden} from "@unibuddy/patron";
import {withTranslation} from "@unibuddy/intl";

import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import withActivity from "shared/Activity/withActivity/withActivity";
import RecordAnalytics from "shared/Analytics/RecordAnalytics";
import withAnalytics from "shared/AnalyticsNew/withAnalytics/withAnalytics";
import {ErrorReportingContext} from "shared/General/components/ErrorReporting/ErrorReportingProvider";
import {setMe} from "shared/Auth/actions/authActions";
import withAuth from "shared/Auth/withAuth/withAuth";
import {rootURL} from "shared/Utils/WhereAmI";
import setWillReplyMutation from "shared/Chat/mutations/setWillReplyMutation";
import {ErrorDisplay} from "shared/General/components/ErrorDisplay/ErrorDisplay";
import {PhoneNumberToast} from "shared/Chat/components/PhoneNumberToast/PhoneNumberToast";
import withChat from "shared/Chat/hocs/withChat/withChat";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import Queue, {QueueItem} from "shared/General/components/Queue/Queue";
import withEmbeddedEvents from "shared/EmbeddedEvents/withEmbeddedEvents/withEmbeddedEvents";
import {HAS_UNIVERSITY_FEATURE_QUERY} from "shared/Feature/HasUniversityFeature";
import {isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity} from "shared/Utils/FeatureUtils";
import {popupNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import DelinkedMessage from "../../components/DelinkedMessageBox/DelinkedMessage";
import withChatInput from "../../components/ChatInputProvider/withChatInput";
import withAskHasApplied from "../../../Applicant/hoc/withAskHasApplied/withAskHasApplied";
import enableSMSMutation from "../../mutations/enableSMSMutation";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import {PopupQuestions} from "../../components/PopupQuestions/PopupQuestions";
import s from "./Chat.pcss";
import {StyledChatComponent} from "../common/StyledChatComponent";
import {Chat as ChatV2} from "../../components/Chat/Chat";
import {PopupQuestionWrapper} from "../../components/PopupQuestions/PopupQuestionWrapper";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_ADD_PHONE_NUMBER_MESSAGE,
    INTL_CONVERSATION_WITH,
    INTL_NOTIFICATION_MESSAGE,
    INTL_REPLY_LATER,
} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import {INTL_INBOX} from "../../../../../translations/inbox";

export class ChatComponent extends Component {
    static defaultProps = {
        messages: [],
    };

    static contextType = ErrorReportingContext;

    state = {
        updatingPhoneNumber: false,
        renderCount: 0,
    };

    componentDidMount() {
        RecordAnalytics.page(this.props, "Chat");
    }

    onSend = async ({text}) => {
        const args = [text];

        try {
            await this.props.onSendMessage(...args);
            let opponentType = get(this.props, "chatGroup.opponent.accountRole");
            if (
                opponentType === "mentor" &&
                get(this.props, "chatGroup.opponent.degreeLevel") === "Alumni"
            ) {
                opponentType = "alumni";
            }
            this.props.analytics.events.messageSent({
                conversationId: get(this.props, "chatGroup.id"),
                opponentId: get(this.props, "chatGroup.opponent.id"),
                opponentType, // TO DO: Replace with group once mentor moved to 3 layer
                chatVersion: "Version 2",
            });

            const isEmbedded = window.parent !== window;

            if (isEmbedded) {
                this.props.embeddedEvents.messageSent();
            }
        } catch (error) {
            this.context.reportError(error, {level: "fatal"});
        }
    };

    onDismissWillReply = async () => {
        await this.props.setWillReply({
            variables: {
                chatGroupId: get(this.props, "chatGroup.id"),
                applicant: get(this.props, "me.anyUser.id"),
                dismissed: true,
            },
        });
        this.props.refetchChatGroupAux();
    };

    async onAddPhoneNumber(phoneNumber, service) {
        this.setState({updatingPhoneNumber: true});
        try {
            const {data} = await this.props.enableSMSMutation({
                variables: {
                    phoneNumber,
                    chatGroupId: get(this.props, "chatGroup.id"),
                    applicant: get(this.props, "me.anyUser.id"),
                    mobileAdded: true,
                    newMessageNotifications: ["email", service],
                },
            });
            if (data.updateApplicant.error) {
                this.setState({
                    updatingPhoneNumber: false,
                    error: true,
                    phoneErrorMessage: data.updateApplicant.error,
                });
                return false;
            }
            this.props.refetchChatGroupAux();
            this.props.dispatch(setMe(data.updateApplicant.me));
            this.props.dispatch(setMe(data.updateApplicantPreferences.me));
            this.setState({updatingPhoneNumber: false});
            return true;
        } catch (e) {
            this.setState({updatingPhoneNumber: false, error: true});
            return false;
        }
    }

    showAuxiliaryQuestion = () => {
        return !get(this.props, "askHasApplied.complete", true);
    };

    shouldShowWillReply = () => {
        // only display this if the opponent is a mentor
        if (get(this.props, "chatGroup.opponent.accountRole", "") === "mentor") {
            // do not show reply scenarios:
            // 1. Ambassador/uni replied
            // 2. Dismissed
            // 3. Phone number added
            const replyReceived =
                get(this.props, "messages", []).filter(
                    (message) => message.sender.accountRole !== "applicant",
                ).length > 1;
            const dismissed = get(this.props, "chatGroupAux.willReply.dismissed", false);
            const mobileAdded = get(this.props, "chatGroupAux.willReply.mobileAdded", false);
            return !replyReceived && !dismissed && !mobileAdded;
        }
        return false;
    };

    backFunction() {
        RecordAnalytics.event(this.props, "Clicked Back in Chat");
        this.props.history.push(`${rootURL(this.props.location.pathname)}/inbox`);
    }

    render() {
        const name = get(this.props, "chatGroup.opponent.firstName", "Buddy");
        const showAddPhoneNumber = this.props.phoneNumber === null;
        const {t} = this.props;
        const phoneNumberText = showAddPhoneNumber
            ? t(INTL_ADD_PHONE_NUMBER_MESSAGE.key, INTL_ADD_PHONE_NUMBER_MESSAGE.defaultValue)
            : t(INTL_NOTIFICATION_MESSAGE.key, INTL_NOTIFICATION_MESSAGE.defaultValue);
        const showAppliedToUniversityQuestion =
            !isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity(
                this.props.features,
                "hideAppliedToUniversityQuestion",
            );

        const queue = (
            <Queue key={this.state.renderCount}>
                {showAppliedToUniversityQuestion ? (
                    <QueueItem enabled={this.showAuxiliaryQuestion()}>
                        {({isOpen, onDismiss}) => {
                            return (
                                <PopupQuestions
                                    askHasApplied={this.props.askHasApplied}
                                    onDismiss={onDismiss}
                                    isOpen={isOpen}
                                />
                            );
                        }}
                    </QueueItem>
                ) : null}
                <QueueItem enabled={this.shouldShowWillReply()}>
                    {({isOpen, onDismiss}) => {
                        const onDismissModal = () => {
                            if (showAddPhoneNumber) {
                                const universitySlug = get(this.props, "universitySlug", "");
                                this.props.analytics.trackEvent(
                                    sharedEvents.phoneNumberCollectionDismissed.name,
                                    {
                                        universitySlug,
                                        popup: popupNames.phoneNumberCollection,
                                        cta: "Close Button",
                                    },
                                );
                            }
                            onDismiss();
                            this.onDismissWillReply();
                        };
                        return (
                            <PopupQuestionWrapper onDismiss={onDismissModal} isOpen={isOpen}>
                                <PhoneNumberToast
                                    isOpen={isOpen}
                                    title={t(
                                        INTL_REPLY_LATER.key,
                                        isFunction(INTL_REPLY_LATER.defaultValue)
                                            ? INTL_REPLY_LATER.defaultValue(name)
                                            : INTL_REPLY_LATER.defaultValue,

                                        {
                                            name,
                                        },
                                    )}
                                    text={phoneNumberText}
                                    onAddPhoneNumber={this.onAddPhoneNumber.bind(this)}
                                    updatingPhoneNumber={this.state.updatingPhoneNumber}
                                    showAddPhoneNumber={showAddPhoneNumber}
                                />
                            </PopupQuestionWrapper>
                        );
                    }}
                </QueueItem>
            </Queue>
        );

        const redirectToRelink = () => {
            // redirecting to 2nd stage signup on settings tab
            this.props.history.replace(this.props.location.pathname, {from: "delinked"});
        };

        if (this.props.messages.length < 1) return <LoadingChat />;

        if (!this.props.loadingHasUniversityFeature) {
            const universitySlug = get(this.props, "universitySlug", "");
            const meAuthState = get(this.props, "authState.me");
            const isDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);

            return (
                <ErrorDisplay
                    error={this.props.messagesQueryError || this.props.messageSendingError}
                    actionButton={{
                        text: "Back",
                        onClick: () => this.backFunction(),
                    }}
                >
                    <div id="chat-page">
                        <PageTitle
                            title={t(
                                `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                                isFunction(INTL_CONVERSATION_WITH.defaultValue)
                                    ? INTL_CONVERSATION_WITH.defaultValue(name)
                                    : INTL_CONVERSATION_WITH.defaultValue,
                                {name},
                            )}
                        />
                        <VisuallyHidden>
                            <PageHeader
                                title={t(
                                    `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                                    INTL_INBOX.defaultValue,
                                )}
                            />
                        </VisuallyHidden>
                        <div className={s.container}>
                            <StyledChatComponent className={s.chat}>
                                <ChatV2
                                    messages={this.props.messages}
                                    user={get(this.props, "authState.me.anyUser")}
                                    chatGroup={this.props.chatGroup}
                                    chatFooterHidden={isDelinked}
                                    chatPopup={
                                        isDelinked ? (
                                            <DelinkedMessage redirectToRelink={redirectToRelink} />
                                        ) : null
                                    }
                                    university={get(this.props, "university", {})}
                                    onSend={this.onSend}
                                    queue={queue}
                                />
                            </StyledChatComponent>
                        </div>
                    </div>
                </ErrorDisplay>
            );
        }
        return (
            <div id="chat-page">
                <PageTitle
                    title={t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {name},
                    )}
                />
                <VisuallyHidden>
                    <PageHeader
                        title={t(
                            `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                            INTL_INBOX.defaultValue,
                        )}
                    />
                </VisuallyHidden>
                <div className={s.container}>
                    <StyledChatComponent
                        className={s.chat}
                        delinked={this.props.chatGroup && this.props.chatGroup.delinked}
                    >
                        <LoadingChat />
                    </StyledChatComponent>
                </div>
            </div>
        );
    }
}

const setWillReplyMutationOptions = {
    name: "setWillReply",
};

const mapStateToProps = (state) => ({
    text: get(state, "chatState.text"),
    phoneNumber: get(state, "authState.me.applicant.phoneNumber"),
});

const HAS_UNIVERSITY_FEATURE_QUERY_OPTIONS = {
    name: "hasUniversityFeature",
    options(props) {
        return {
            variables: {
                feature: "hideAppliedToUniversityQuestion",
                universitySlug: get(props, "universitySlug", ""),
            },
            fetchPolicy: "network-only",
        };
    },
    props({hasUniversityFeature: {university, loading}}) {
        return {
            features: get(university, "config.features", []),
            loadingHasUniversityFeature: loading,
        };
    },
};

export default compose(
    connect(mapStateToProps),
    withRouter,
    withAuth,
    withAnalytics,
    withChat(),
    withEmbeddedEvents,
    withAskHasApplied,
    withChatInput,
    withTranslation([IntlNamespaces.CHAT, IntlNamespaces.INBOX]),
    withActivity("onix", "Chat"),
    graphql(setWillReplyMutation, setWillReplyMutationOptions),
    graphql(enableSMSMutation, {name: "enableSMSMutation"}),
    graphql(HAS_UNIVERSITY_FEATURE_QUERY, HAS_UNIVERSITY_FEATURE_QUERY_OPTIONS),
)(ChatComponent);
